import { useEffect, useState } from 'react';
import { API } from '@/lib/apiConfig';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorBanner from '../components/common/ErrorBanner';

type Rec = { companyId: string; deviceId: string; timestamp: string; energy_kWh: number; water_liters: number; temp_celsius: number };

export function IoTMeters() {
  const [deviceId, setDeviceId] = useState('meter-1');
  const [rows, setRows] = useState<Rec[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const register = async () => {
    setLoading(true);
    setError(null);
    try {
      const rec: Rec = { companyId: 'demo', deviceId, timestamp: new Date().toISOString(), energy_kWh: Math.random()*10, water_liters: Math.random()*50, temp_celsius: 20+Math.random()*5 };
      const res = await fetch(API.iot + '/iot/meter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(rec)
      });
      
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }
      
      setRows((r) => [rec, ...r].slice(0, 1000));
    } catch (e) {
      const error = e instanceof Error ? e : new Error('Failed to register IoT reading');
      setError(error);
      console.error('IoT registration failed:', e);
    } finally {
      setLoading(false);
    }
  };

  const data = rows.slice(0, 50).reverse().map((r) => ({ t: new Date(r.timestamp).toLocaleTimeString(), energy: r.energy_kWh }));
  const forecast = 5; // dummy baseline
  const last = rows[0]?.energy_kWh || 0;
  const over = last > forecast * 1.25;

  return (
    <div className="space-y-3">
      <div className="font-orbitron text-evat-gold">IoT Meters</div>
      
      <ErrorBanner error={error} onRetry={register} showRetry={!!error} />
      
      <div className="flex gap-2 text-sm items-center flex-wrap">
        <input
          value={deviceId}
          onChange={(e) => setDeviceId(e.target.value)}
          className="bg-black border border-evat-neon text-evat-neon px-2 py-1 rounded"
          aria-label="Device ID"
          disabled={loading}
        />
        <button
          onClick={register}
          disabled={loading}
          className="px-3 py-1 rounded border border-evat-neon text-evat-neon hover:shadow-neon disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Registering...' : 'Register Reading'}
        </button>
        <div className={`text-sm ${over ? 'text-red-400' : 'text-emerald-400'}`}>
          Latest: {last.toFixed(2)} kWh {over ? '(>25% over baseline)' : ''}
        </div>
      </div>
      
      {loading && <LoadingSpinner size="sm" text="Sending reading..." />}
      <div className="w-full h-48">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
            <XAxis dataKey="t" stroke="#d4af37" />
            <YAxis stroke="#d4af37" />
            <Tooltip contentStyle={{ background:'#000', border:'1px solid #00FF88', color:'#fff' }} />
            <Line type="monotone" dataKey="energy" stroke="#00FF88" dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="border border-neutral-700 rounded p-2 text-xs max-h-56 overflow-auto">
        {rows.slice(0,20).map((r,i) => (
          <div key={i} className="flex justify-between"><span>{new Date(r.timestamp).toLocaleString()}</span><span>{r.energy_kWh.toFixed(2)} kWh</span></div>
        ))}
      </div>
    </div>
  );
}

export default IoTMeters;














